import { Proposal } from '@modules/proposal/Blocks/types';
import { ProposalBlockFormData } from '../../ProposalForm';

// Agreed maximum of top-level entries in the client-facing proposal navigation.
export const MAX_TOP_LEVEL_SECTIONS = 4;

export interface NavBlockItem {
  block: ProposalBlockFormData;
  index: number;
}

// A section in the parent tree: its nested sections (any depth) under `children`. Leaves have none.
export interface NavTreeNode extends NavBlockItem {
  children: NavTreeNode[];
}

export type NavEntry =
  | ({ kind: 'block' } & NavBlockItem)
  | { kind: 'group'; name: string; items: NavBlockItem[] }
  // Parent section: a tree of the sections nested under it, each level expand/collapse in the client menu
  | ({ kind: 'parent' } & NavTreeNode);

// Pricing and signing are what the client acts on, so they are always visible: on the page they are never nested under
// a parent or the section above (so a collapsed section can't hide them) and never start collapsed; in the client menu
// they always get their own entry (not under a parent, not hidden). Their stored parentId / hideInNav are ignored, so
// this applies to existing proposals too.
// (Read at call time: Blocks/types imports every block, which import this file, so a module-level list could see it
// half-loaded.)
export function isAlwaysVisible(block?: Pick<ProposalBlockFormData, 'type'> | null): boolean {
  return !!block && (block.type === Proposal.BlockType.pricing || block.type === Proposal.BlockType.signer);
}

// Left out of the client menu ("Hide from client navigation menu"), never for pricing / signing.
export function isHiddenInNav(block: ProposalBlockFormData): boolean {
  return !!block.hideInNav && !isAlwaysVisible(block);
}

// Plain-text menu label of a block: the title without its tags, with entities such as &amp; decoded.
export function navLabel(block: ProposalBlockFormData): string {
  const withoutTags = (block.title || '').replace(/<[^>]*>/g, ' ');
  const text = decodeEntities(withoutTags).replace(/\s+/g, ' ').trim();
  return text || (block.variation || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// "Tile &amp; Stone" → "Tile & Stone". DOMParser builds an inert document, so nothing in the title runs.
function decodeEntities(text: string): string {
  if (!text.includes('&')) return text;
  return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent || '';
}

/**
 * Parent-section tree shared by the client menu and the builder sidebar (any depth, like a collapsible JSON tree).
 * A block is a parent when it is marked `isParentSection` and shown in the menu. A block (parent or not) is nested
 * when its `parentId` points at such a parent. Links that would make a loop (a section under itself or under one of
 * its own descendants) are ignored, and a missing / unmarked / hidden parent leaves its children standalone.
 */
export function resolveParents(blocks: ProposalBlockFormData[], options: { includeHidden?: boolean; implicitSections?: boolean } = {}) {
  const parentIndexById = new Map<string, number>();
  blocks.forEach((block, index) => {
    if (block.isParentSection && (options.includeHidden || !isHiddenInNav(block)) && block.id && !parentIndexById.has(block.id)) {
      parentIndexById.set(block.id, index);
    }
  });
  // Block index → index of the parent it is nested under (only valid, loop-free links)
  const parentOf = new Map<number, number>();
  blocks.forEach((block, index) => {
    if (!block.parentId || isAlwaysVisible(block)) return;
    const parentIndex = parentIndexById.get(block.parentId);
    if (parentIndex !== undefined && parentIndex !== index) parentOf.set(index, parentIndex);
  });
  // Page content tree: a block without an explicit parent that isn't a section belongs to the nearest section above
  // it (the same grouping the builder sidebar shows and section drag-sort uses).
  if (options.implicitSections) {
    let lastSection: number | undefined;
    blocks.forEach((block, index) => {
      if (block.isSection) lastSection = index;
      else if (!parentOf.has(index) && lastSection !== undefined && !isAlwaysVisible(block)) parentOf.set(index, lastSection);
    });
  }
  // Break loops: walk each chain up; a chain that comes back to its start loses the start's link.
  blocks.forEach((_, index) => {
    const seen = new Set<number>([index]);
    let current = parentOf.get(index);
    while (current !== undefined) {
      if (seen.has(current)) {
        parentOf.delete(index);
        break;
      }
      seen.add(current);
      current = parentOf.get(current);
    }
  });
  // Depth of each block in the tree (0 = top level)
  const depthOf = (index: number) => {
    let depth = 0;
    for (let current = parentOf.get(index); current !== undefined; current = parentOf.get(current)) depth++;
    return depth;
  };
  // Indexes of a block's descendants (used to keep the parent picker from creating loops)
  const descendantsOf = (index: number): Set<number> => {
    const result = new Set<number>();
    const walk = (parent: number) =>
      parentOf.forEach((p, child) => {
        if (p === parent && !result.has(child)) {
          result.add(child);
          walk(child);
        }
      });
    walk(index);
    return result;
  };
  const hasChildren = new Set<number>(parentOf.values());
  return { parentIndexById, parentOf, depthOf, descendantsOf, hasChildren };
}

/**
 * Collapse tree of the proposal PAGE (any depth): explicit parents (hidden-from-menu ones too) plus each section's
 * following blocks. Collapsing a block hides all its descendants.
 */
export function contentTree(blocks: ProposalBlockFormData[]) {
  return resolveParents(blocks, { includeHidden: true, implicitSections: true });
}

// Shapes the proposal navigation only: blocks sharing a navGroup (trimmed, case-insensitive) are
// collected under one entry placed where the group's first block appears; every other block keeps
// its own entry. Page content and block order are untouched.
// Blocks marked `hideInNav` are left out (a group whose blocks are all hidden gets no entry); they
// stay on the page. `index` is still the block's position in `blocks`, so scrolling is unaffected.
//
// Parent sections come first: a parent is one entry at its own position with its nested sections (in page order)
// under it, wherever those sections sit on the page; nested sections get no entry of their own and ignore navGroup.
export function buildNavEntries(blocks: ProposalBlockFormData[]): NavEntry[] {
  const entries: NavEntry[] = [];
  const groups = new Map<string, Extract<NavEntry, { kind: 'group' }>>();
  const { parentOf } = resolveParents(blocks);
  const childIndexes = new Map<number, number[]>();
  blocks.forEach((block, index) => {
    const parentIndex = parentOf.get(index);
    if (parentIndex === undefined || isHiddenInNav(block)) return;
    childIndexes.set(parentIndex, [...(childIndexes.get(parentIndex) || []), index]);
  });
  // Children in page order; a nested parent carries its own subtree (any depth)
  const node = (index: number): NavTreeNode => ({ block: blocks[index], index, children: (childIndexes.get(index) || []).map(node) });

  blocks.forEach((block, index) => {
    if (isHiddenInNav(block)) return;
    if (parentOf.has(index)) return; // listed under its parent
    if (block.isParentSection) {
      entries.push({ kind: 'parent', ...node(index) });
      return;
    }
    const name = block.navGroup?.trim();
    if (!name) {
      entries.push({ kind: 'block', block, index });
      return;
    }
    let group = groups.get(name.toLowerCase());
    if (!group) {
      group = { kind: 'group', name, items: [] };
      groups.set(name.toLowerCase(), group);
      entries.push(group);
    }
    group.items.push({ block, index });
  });

  return entries;
}

// Top-level entries are groups plus ungrouped sections — plain blocks render nested under them.
export function countTopLevelSections(entries: NavEntry[]): number {
  return entries.filter(entry => entry.kind === 'group' || entry.kind === 'parent' || entry.block.isSection).length;
}

export interface BuilderGroupRow {
  // Group name to print as a heading above this row (only on the first block of a run).
  label?: string;
  // This block belongs to a group, so it renders indented under the heading.
  grouped: boolean;
}

// Builder sidebar: the list stays in block order (its rows map 1:1 to block indexes, which the
// drag-sort depends on), so a group heading is printed above each CONTIGUOUS run of blocks sharing
// a group. Scattered members therefore repeat the heading — a hint to move them together — while
// the client navigation still merges them into a single entry.
export function builderGroupRows(blocks: ProposalBlockFormData[]): BuilderGroupRow[] {
  let previous: string | undefined;

  return blocks.map(block => {
    const name = block.navGroup?.trim();
    const key = name?.toLowerCase();
    const row: BuilderGroupRow = { grouped: !!name, label: name && key !== previous ? name : undefined };
    previous = key;
    return row;
  });
}
